import type { CardSummary as Card } from "@hobbyshopify/backend-client";
import { useState } from "react";
import { cardTitle, formatCents } from "../core/primitives/card-display";

function centsToInput(cents: number | null | undefined): string {
  return cents == null ? "" : (cents / 100).toFixed(2);
}

function inputToCents(value: string): number | null {
  const parsed = Number(value);
  if (!value.trim() || !Number.isFinite(parsed)) return null;
  return Math.round(parsed * 100);
}

/**
 * The two numbers a merchant sets on a card after it exists: what it asks, and
 * what it will not go below.
 *
 * Identity is not editable here on purpose — changing what a card *is* moves it
 * to a different identity key, which is a re-match rather than an edit, and the
 * review screen owns that. Named for the prices rather than for "edit" because
 * prices are all it has ever touched.
 */
export function SetPriceModal({
  card,
  busy,
  error,
  onSave,
  onCancel,
}: {
  card: Card;
  busy?: boolean;
  error?: string | null;
  onSave: (next: { priceCents: number | null; floorPriceCents: number | null }) => void;
  onCancel: () => void;
}) {
  const [price, setPrice] = useState(centsToInput(card.currentPriceCents));
  const [floor, setFloor] = useState(centsToInput(card.floorPriceCents));

  const priceCents = inputToCents(price);
  const floorCents = inputToCents(floor);
  const floorValid = priceCents == null || floorCents == null || floorCents <= priceCents;

  return (
    <div className="scrim items-end justify-center p-4 sm:items-center">
      <div className="modal relative w-full max-w-sm p-6">
        <h2 className="mb-1 text-base font-semibold text-white">Set price</h2>
        <p className="mb-4 truncate text-sm text-muted">{cardTitle(card)}</p>

        <div className="space-y-3">
          <div>
            <div className="mb-1 block text-xs font-medium text-muted">Price</div>
            <input
              type="number"
              min="0"
              step="0.01"
              className="field"
              value={price}
              onChange={(e) => setPrice(e.target.value)}
            />
            <p className="mt-1 text-xs text-faint">What the shop asks. Clearing it takes the card off sale.</p>
          </div>

          <div>
            <div className="mb-1 block text-xs font-medium text-muted">Floor price</div>
            <input
              type="number"
              min="0"
              step="0.01"
              className={`field ${floorValid ? "" : "border-danger focus:border-danger"}`}
              value={floor}
              onChange={(e) => setFloor(e.target.value)}
              aria-invalid={!floorValid}
            />
            {floorValid ? (
              // The floor is why a customer scan can be answered without the
              // merchant, so it is worth saying what setting one actually does.
              <p className="mt-1 text-xs text-faint">
                Your break-even. With one set, customers scanning this card are quoted automatically while the price
                stays above it.
              </p>
            ) : (
              <p className="field-error text-xs">The floor cannot be above the price.</p>
            )}
          </div>
        </div>

        {card.currentPriceCents != null && (
          <p className="mt-3 text-xs text-faint">Currently {formatCents(card.currentPriceCents)}.</p>
        )}
        {error && <p className="note note-danger mt-3 text-danger">{error}</p>}

        <div className="mt-6 flex gap-3">
          <button type="button" onClick={onCancel} disabled={busy} className="btn-secondary flex-1">
            Cancel
          </button>
          <button
            type="button"
            onClick={() => onSave({ priceCents, floorPriceCents: floorCents })}
            disabled={busy || !floorValid}
            className="btn-primary flex-1"
          >
            {busy ? "Saving…" : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
}
